// src/shared/api/interceptors/namespace.ts
import { type InternalAxiosRequestConfig } from 'axios';
import { axiosInstance } from '../client';
import { tokenService } from '@/shared/lib/token/tokenService';
import { env } from '@/shared/config/env';

export const normalizeUrl = (
  url: string,
  baseURL: string,
  namespaceOverride?: 'merchant' | 'system',
): string => {
  if (url.startsWith('http://') || url.startsWith('https://')) return url;

  let path = url.startsWith('/') ? url.substring(1) : url;
  const explicitNamespace = path.match(/^api\/v2\/(merchant|system)\//)?.[1] as
    | 'merchant'
    | 'system'
    | undefined;
  // Explicit API paths remain authoritative. The override protects System
  // Admin pages from a stale merchant namespace stored by an earlier session.
  const namespace =
    explicitNamespace ?? namespaceOverride ?? tokenService.getApiNamespace();

  path = path.replace(/^api\/v1\//, '');
  path = path.replace(/^api\/v2\/merchant\//, '');
  path = path.replace(/^api\/v2\/system\//, '');
  path = path.replace(/^api\/v2\//, '');

  const baseUrlObj = new URL(baseURL);

  if (baseUrlObj.pathname.includes('/api/v2')) {
    return `${baseURL}/${namespace}/${path}`;
  }
  return `${baseUrlObj.origin}/api/v2/${namespace}/${path}`;
};

/**
 * Rewrite short service paths onto the caller's API namespace
 * (/api/v2/merchant/… or /api/v2/system/…) and stamp the x-store header.
 *
 * Exported as a function so it is reachable through a real binding — see the
 * note in auth.ts for why a module-scope registration was unsafe.
 */
export function registerNamespaceInterceptor(): void {
  axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (config.url) {
      const baseURL = config.baseURL ?? env.API_URL;
      const isSystemAdminRoute =
        window.location.pathname === '/admin' ||
        window.location.pathname.startsWith('/admin/');
      config.url = normalizeUrl(
        config.url,
        baseURL,
        isSystemAdminRoute ? 'system' : undefined,
      );
      config.baseURL = undefined;
    }

    const pathParts = window.location.pathname.split('/');
    const storeIndex = pathParts.indexOf('store');
    const storeId =
      storeIndex !== -1 && pathParts[storeIndex + 1]
        ? pathParts[storeIndex + 1]
        : null;

    if (storeId) {
      config.headers['x-store'] = storeId;
      config.headers['x-store-id'] = storeId;
    }

    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
    (error) => Promise.reject(error),
  );
}
